'use client';

import React, { MouseEventHandler, useEffect, useState } from 'react';

import { Button as DesignSystemButton } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import StarFillIcon from '@flipster/react/icon/generated/StarFillIcon';
import StarOutlineIcon from '@flipster/react/icon/generated/StarOutlineIcon';
import { css } from '@flipster/styled-system/css';
import { circle, grid, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { ResolutionString } from '../../lib/charting_library';
import { DefaultFavoriteIntervals } from '../misc/constants';
import { useTradingViewDataStorage } from '../misc/data-manager';
import { useTvHeaderContext } from './';
import { Button, CheckItem } from './button';
import { Dropdown } from './dropdown';

const MaxFavoriteCount = 8;

interface Interval {
  label: string;
  value: ResolutionString;
}

export const WebIntervals = () => {
  const tv = useTvHeaderContext();

  const [interval] = useTradingViewDataStorage({
    key: 'interval',
  });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [tempFavoriteInterval, setTempFavoriteInterval] = useState<Record<ResolutionString, boolean>>({});
  const [favoriteCountError, setFavoriteCountError] = useState<boolean>(false);
  const [favoriteIntervals, setFavoriteIntervals] = useTradingViewDataStorage({
    key: 'favoriteIntervals',
  });

  const onClickResolution: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!tv?.enabled) return;
    const currentResolution = e.currentTarget.value as ResolutionString;
    segment.track(ChartEvent.TimeframeClick, { interval: currentResolution });
    tv.activeChart?.setResolution(currentResolution);
  };

  const onClickFavorite: MouseEventHandler<HTMLButtonElement> = (e) => {
    const currentResolution = e.currentTarget.value as ResolutionString;
    if (
      !tempFavoriteInterval[currentResolution] &&
      Object.values(tempFavoriteInterval).filter((val) => val).length === MaxFavoriteCount
    ) {
      setFavoriteCountError(true);
      return;
    }
    setTempFavoriteInterval((prevState) => ({ ...prevState, [currentResolution]: !prevState[currentResolution] }));
    setFavoriteCountError(false);
  };

  const currentInterval = interval.value;

  useEffect(() => {
    (async () => {
      const nextInterval = await interval.asyncValue();
      if (tv.interval !== nextInterval) {
        tv.activeChart?.setResolution(nextInterval);
      }
    })();
  }, [interval, tv.activeChart, tv.interval]);

  return (
    <>
      {PossibleIntervals.filter((i) => favoriteIntervals.value.includes(i.value)).map(({ label, value }) => (
        <Button
          value={value}
          key={value}
          active={value === currentInterval}
          onClick={onClickResolution}
          css={{ textStyle: 'Text/M/Regular' }}
        >
          {label}
        </Button>
      ))}
      <Dropdown
        withClickClose={!isEditing}
        onClose={() => {
          setIsEditing(false);
          setFavoriteCountError(false);
        }}
        trigger={
          <Button css={{ textStyle: 'Text/M/Regular' }} withChevron>
            {favoriteIntervals.value.every((i) => i !== currentInterval)
              ? IntervalValueLabelMapper[currentInterval]
              : null}
          </Button>
        }
      >
        <div className={css({ w: 280, maxW: '100dvw' })}>
          <div className={hstack({ justify: 'space-between', mb: 12 })}>
            <p className={css({ textStyle: 'Text/M/Strong', color: 'inverse.white' })}>Select interval</p>
            <TextButton
              color='neutral'
              size='small'
              type='button'
              onClick={(e) => {
                e.stopPropagation(); // 전파시 닫힘 방지
                if (isEditing) {
                  setFavoriteIntervals(
                    Object.entries(tempFavoriteInterval)
                      .filter(([, value]) => value)
                      .map(([key]) => key as ResolutionString),
                  );
                  return;
                }
                setTempFavoriteInterval(
                  favoriteIntervals.value.reduce((acc: Record<ResolutionString, boolean>, cur: ResolutionString) => {
                    acc[cur] = true;
                    return acc;
                  }, {}),
                );
                setIsEditing((prev) => !prev);
              }}
              label={isEditing ? 'Save' : 'Edit'}
            />
          </div>
          <div className={grid({ w: '100%', columns: 4, gap: 6 })}>
            {PossibleIntervals.map((i) => {
              const isTempFavorite = tempFavoriteInterval[i.value];
              const isFavorite = isEditing ? isTempFavorite : favoriteIntervals.value.includes(i.value);
              return (
                <DesignSystemButton
                  hierarchy='secondary'
                  color={isFavorite ? 'accent' : 'neutral'}
                  size='small'
                  key={i.value}
                  value={i.value}
                  aria-checked={isFavorite}
                  onClick={isEditing ? onClickFavorite : onClickResolution}
                  css={{ pos: 'relative' }}
                  label={
                    <>
                      {i.label}
                      {isEditing && (
                        <div
                          className={circle({
                            zIndex: 1,
                            pos: 'absolute',
                            top: -6,
                            right: -6,
                            bgColor: 'background.default.level-4',
                            size: 20,
                          })}
                        >
                          {isFavorite ? (
                            <StarFillIcon size={16} color={token('colors.content.state.warning')} />
                          ) : (
                            <StarOutlineIcon size={16} />
                          )}
                        </div>
                      )}
                    </>
                  }
                />
              );
            })}
          </div>
          {isEditing && favoriteCountError && (
            <p className={hstack({ textStyle: 'Text/S/Regular', color: 'content.state.negative', mt: 4, gap: 4 })}>
              <CautionCircleIcon size={14} /> {`Select up to ${MaxFavoriteCount}`}
            </p>
          )}
        </div>
      </Dropdown>
    </>
  );
};

export const WebviewIntervals = () => {
  const tv = useTvHeaderContext();

  const [interval] = useTradingViewDataStorage({
    key: 'interval',
  });

  const onClickResolution: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!tv?.enabled) return;
    const currentResolution = e.currentTarget.value as ResolutionString;
    tv.activeChart?.setResolution(currentResolution);
    segment.track(ChartEvent.TimeframeClick, { interval: currentResolution });
  };

  useEffect(() => {
    (async () => {
      const nextInterval = await interval.asyncValue();
      if (nextInterval !== tv.interval) {
        tv.activeChart?.setResolution(nextInterval);
      }
    })();
  }, [interval, tv.activeChart, tv.interval]);

  const currentInterval = interval.value;

  return (
    <>
      {DefaultFavoriteIntervals.map((r) => (
        <Button
          value={r}
          key={r}
          active={r === currentInterval}
          onClick={onClickResolution}
          css={{ textStyle: 'Text/M/Regular' }}
        >
          {IntervalValueLabelMapper[r]}
        </Button>
      ))}
      <Dropdown
        withClickClose
        trigger={
          <Button css={{ textStyle: 'Text/M/Regular' }} withChevron>
            {DefaultFavoriteIntervals.every((r) => r !== currentInterval)
              ? IntervalValueLabelMapper[currentInterval]
              : null}
          </Button>
        }
        css={{ minW: 100 }}
      >
        <div className={stack({ gap: 6 })}>
          {PossibleIntervals.filter((i) => !DefaultFavoriteIntervals.includes(i.value)).map((i) => (
            <CheckItem checked={i.value === currentInterval} value={i.value} key={i.value} onClick={onClickResolution}>
              {i.label}
            </CheckItem>
          ))}
        </div>
      </Dropdown>
    </>
  );
};

const IntervalValueLabelMapper = {
  '1': '1m',
  '3': '3m',
  '5': '5m',
  '15': '15m',
  '30': '30m',
  '60': '1h',
  '120': '2h',
  '240': '4h',
  '360': '6h',
  '720': '12h',
  '1D': '1D',
  '1W': '1W',
  '1M': '1M',
} as Record<ResolutionString, string>;

const PossibleIntervals: Interval[] = Object.entries(IntervalValueLabelMapper).map(([value, label]) => ({
  label,
  value: value as ResolutionString,
}));
